import { Suspense, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import {CtPlatform, CtPlatformBaseItem, UserSettings} from 'models/general';
import {
  ImportActiveSection,
  StoreContext, ImportSection, StoreState,
} from 'models/import';
import 'styles/styles.scss';
import ScrollTop from 'components/scrollTop';
import { ThemeName } from 'models/theme';
import { TableSettings } from 'models/table';
import RouteWrapper from 'components/routeWrapper';
import {getLaminasApiData} from './services/api';
import Spinner, {SpinnerModifier} from './components/spinner';
import {isEmptyObject} from './services/util';
import ScrollToTopOnNavigate from 'components/scrollToTopOnNavigate';

function App() {
  const [state, setState] = useState({
    step: 0,
    setStep: (step: number) => {
      setState((s: StoreState) => ({ ...s, step }));
    },
    platform: undefined,
    setPlatform: (platform: CtPlatform) => {
      setState((s: StoreState) => ({ ...s, platform }));
    },
    baseItems: undefined,
    setBaseItems: (baseItems: CtPlatformBaseItem[]) => {
      setState((s: StoreState) => ({ ...s, baseItems }));
    },
    importTableSettings: undefined,
    setImportTableSettings: (importTableSettings: TableSettings | undefined) => {
      setState((s: StoreState) => ({ ...s, importTableSettings }));
    },
    activeSection: undefined,
    setActiveSection: (activeSection: ImportActiveSection<unknown>) => {
      setState((s: StoreState) => ({ ...s, activeSection }));
    },
    importType: undefined,
    setImportType: (importType: ImportSection<string>) => {
      setState((s: StoreState) => ({ ...s, importType }));
    },
    importName: undefined,
    setImportName: (importName: ImportSection<string>) => {
      setState((s: StoreState) => ({ ...s, importName }));
    },
    userSettings: ('userSettings' in window && !isEmptyObject(window.userSettings))
      ? window.userSettings as UserSettings
      : undefined,
    setUserSettings: (userSettings: UserSettings) => {
      setState((s: StoreState) => ({ ...s, userSettings }));
    },
    themeName: ThemeName.LIGHT,
    setThemeName: (themeName: ThemeName) => {
      setState((s: StoreState) => ({ ...s, themeName }));
    },
    isProPage: undefined,
    setIsProPage: (isProPage: boolean) => {
      setState((s: StoreState) => ({ ...s, isProPage }));
    },
    isIntegrationsPage: undefined,
    setIsIntegrationsPage: (isIntegrationsPage: boolean) => {
      setState((s: StoreState) => ({ ...s, isIntegrationsPage }));
    },
  } as StoreState);

  const getCookie = (name: string) => document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');

  useEffect(() => {
    const root = document.querySelector('html');
    const themeCookie = getCookie('cointracking_theme');

    if (root) {
      const themeVal = themeCookie && themeCookie.length ? `_${themeCookie}` : ThemeName.LIGHT;
      root.dataset.theme = themeVal
      state.setThemeName(themeVal);
    }

    if (state.userSettings === undefined) {
      getLaminasApiData<UserSettings>('/main-app/user-settings').then((res) => {
        const userSettingsData = res.data;
        state.setUserSettings(userSettingsData || undefined);
      });
    }
  }, []);

  const basename = process.env.BASENAME || '';

  return (state.userSettings === undefined)
    ? (
      <main className="ct_main">
        <div className="ct_table _isLoading _isLoadingMain">
          <Spinner modifiers={[SpinnerModifier.BG_TRANSPARENT]}/>
        </div>
      </main>
    )
    : (
      <main className="ct_main">
        <Suspense fallback={null}>
          <StoreContext.Provider value={state}>
            <BrowserRouter basename={basename}>
              <ScrollToTopOnNavigate />
              <RouteWrapper />
            </BrowserRouter>
          </StoreContext.Provider>
          <ScrollTop />
        </Suspense>
      </main>
    )
  ;
}

export default App;
